import { useState, useEffect, useRef } from 'react';
import { FileStatus } from '../../../data/enums';
import { S3Client, ListObjectsV2Command, GetObjectCommand, ListObjectsV2Output } from "@aws-sdk/client-s3";
import { useAuth } from '../../../context/auth-provider';
import { Upload } from '@aws-sdk/lib-storage';
import { trackTelemetryEvent, getUserGroupsFromIdToken } from '../../../utils/telemetry';

const S3_MAX_KEYS_TO_LIST = 100;

// v2 props: single file per snapshot
interface IuseS3AmaManagerProps {
  bucketName: string;
  clientId: string;               // required client identifier
  academicYear: string;           // YYYY
  snapshotDate: string;           // YYYYMMDD
  existingFileKey?: string | null; // if a file already present (bronze/ingest path) for status init
  existingStatus?: FileStatus;     // optional override starting status
}

interface IuseAmaS3ManagerResult {
  isS3initialized: boolean;
  fileUploads: Array<IFileUpload> | null;
  uploadAll: () => Promise<boolean>;
  uploadSingle: () => void; // single-file upload
  isProcessing: boolean;
  setFileForUpload: (file: File | null) => void;
  // Explicit status setter exposed for view-level mapping from snapshot lifecycle -> file tile state.
  // Guards against unintended downgrades (e.g. Uploaded -> Processing) and ignores while Uploading in progress.
  setFileStatus: (status: FileStatus) => void;
  listObjectsAtPrefix: (prefix: string) => Promise<Array<IFileUpload>>;
  downloadObject: (key: string, customFilename?: string) => Promise<void>;
  downloadJsonFile: (key: string) => Promise<string>;
  getObjectsForBucket: (bucket: string, prefix: string) => Promise<Array<string>>;
}

export const useAmaS3Manager = (props: IuseS3AmaManagerProps): IuseAmaS3ManagerResult => {

  // Single logical category name retained for compatibility with FileUploaderItem
  const SINGLE_FILE_CATEGORY = 'snapshot';

  const s3Ref = useRef<any | null>();
  const [isS3initialized, setIsS3initialized] = useState<boolean>(false);
  const [fileUploads, setFileUploads] = useState<Array<IFileUpload> | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const { user, refresh } = useAuth();

  // useEffect(() => {

  //   try {

  //     console.log(`useS3Manager.useEffect :: mount`);
  //     const credentials = getCredentials()
  //     if (!credentials) {
  //       // console.error(`useS3Manager.useEffect :: credentials not found`);
  //       return;
  //     }

  //     credentials.get(function(err:any) {        
  //       s3Ref.current = new S3Client({
  //         region: process.env.REACT_APP_AWS_REGION!,
  //         credentials: {
  //           accessKeyId: credentials.accessKeyId,
  //           secretAccessKey: credentials.secretAccessKey,
  //           sessionToken: credentials.sessionToken 
  //         }
  //       });
  //       setIsS3initialized(true);
  //       // console.log(`useS3Manager.useEffect :: mount :: s3Ref initalized`);
  //     });
      
  //   } catch(e) {
  //     console.error(`useS3Manager.useEffect :: error -  ${e}`);
  //   }

  //   // return () => { console.log(`useS3Manager.useEffect :: unmount`); };

  // }, []);

  useEffect(() => {
    const initS3 = async () => {
      if (!user || !user.accessKeyId || !user.secretAccessKey || !user.sessionToken) {
        await refresh();
      }
      if (!user?.accessKeyId || !user.secretAccessKey || !user.sessionToken) {
        console.error('useAmaS3Manager :: credential refresh failed');
        return;
      }
      s3Ref.current = new S3Client({
        region: process.env.REACT_APP_AWS_REGION!,
        credentials: {
          accessKeyId: user.accessKeyId,
          secretAccessKey: user.secretAccessKey,
          sessionToken: user.sessionToken,
        },
        // Prevent automatic checksum opt-in on multipart create unless explicitly required.
        requestChecksumCalculation: 'WHEN_REQUIRED' as any,
        responseChecksumValidation: 'WHEN_REQUIRED' as any,
      });
      setIsS3initialized(true);
    };
    initS3().catch((err) => console.error('useAmaS3Manager.initS3 :: error', err));
  }, [user, refresh]);

  // Initialize / refresh single file entry when identifying props change
  useEffect(() => {
    if (isProcessing) return; // avoid overwriting during an active upload cycle
    const startingStatus: FileStatus = props.existingStatus || FileStatus.Empty;
    const single: IFileUpload = {
      Category: SINGLE_FILE_CATEGORY,
      FileStatus: startingStatus,
      FileForUpload: (fileUploads && fileUploads[0]?.FileStatus === FileStatus.Collected) ? fileUploads[0].FileForUpload : null,
      FileSize: (fileUploads && fileUploads[0]?.FileStatus === FileStatus.Collected) ? fileUploads[0].FileSize : 0,
      UploadProgress: 0,
      BronzePath: props.existingFileKey || undefined,
    } as IFileUpload;
    setFileUploads([single]);
  }, [props.clientId, props.snapshotDate, props.academicYear, props.existingFileKey, props.existingStatus, isProcessing]);

  //TODO: switch to return void
  const uploadAll = async (): Promise<boolean> => {    

    if (!user) {
      console.error(`useS3Manager.uploadAll :: ERROR :: user not found`);
      return false;
    }

    if (!fileUploads) return false;
    const pendingUploads = fileUploads.filter(item => item.FileStatus === FileStatus.Collected );
    const uploads = [] as Promise<IFileUpload>[];

    pendingUploads?.forEach((file, index) => {      
      console.log(`useS3Manager.uploadAll :: file: ${file.Category} - ${file.FileStatus}`);
      uploads.push(uploadSingle());
    });

    //promise all settled
    const results = await Promise.allSettled(uploads);

    // resolved/fulfilled Promises' values
    const fulfilled = results.filter((result): result is PromiseFulfilledResult<IFileUpload> => result.status === 'fulfilled').map(result => result.value)
    
    // rejected Promises' reasons
    const rejected = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected').map(result => result.reason)
    
    if (rejected.length>0 && fulfilled.length==0) {
      // all failed
      console.error(`useS3Manager.uploadAll :: ERROR :: all uploads failed!`);
      console.log(rejected)
      return false;

    } else if (rejected.length>0 && fulfilled.length>0) {
      // some failed
      console.error(`useS3Manager.uploadAll :: ERROR :: [${rejected.length}] uploads failed!`);
      console.log(rejected) 
      //console.log(fulfilled)
      return true;

    } else if (rejected.length==0 && fulfilled.length>0) {
      // all success
      console.log(`useS3Manager.uploadAll :: all uploads complete`);
      return true;
    }

    return false;    
  };

  const uploadSingle = async (): Promise<IFileUpload> => {
      
      if (!fileUploads) {
        //console.log(`useS3Manager.uploadSingle - fileUploads not found`);
        return { Category: SINGLE_FILE_CATEGORY, FileStatus: FileStatus.Error, FileSize: 0, UploadProgress: 0 };
      }
      const pendingUpload = fileUploads[0];
      if (!pendingUpload) {
        //console.log(`useS3Manager.uploadSingle - pendingUpload at category [${category}] not found`);
        return { Category: SINGLE_FILE_CATEGORY, FileStatus: FileStatus.Error, FileSize: 0, UploadProgress: 0 };
      } 

      const params = {
        Bucket: props.bucketName,
        Key: getS3Key(props.clientId, props.academicYear, props.snapshotDate, pendingUpload!.FileForUpload!),
        Body: pendingUpload!.FileForUpload!,
        type: pendingUpload!.FileForUpload!.type,
      };

      const startedAt = performance.now();
      try {
        const upload = new Upload({
          client:s3Ref.current,
          params:params
        });
        upload.on("httpUploadProgress", (progress:any) => {
          setFileUploadStatus(progress.loaded/progress.total);
        });

        //init file upload status while upload client starts
        setFileUploadStatus(0);
        console.log(`useS3Manager.uploadSingle :: calling done`);
        const completeMultiPartUpload = await upload.done();
        console.log(`useS3Manager.uploadSingle :: done`);
        upload.removeAllListeners();

        if (user) {
          trackTelemetryEvent({
            userSub: user.userId,
            userEmail: user.email,
            userGroups: getUserGroupsFromIdToken(user.idToken),
            authToken: user.idToken,
            actionType: 'file_upload',
            actionTarget: params.Key,
            status: 'success',
            latencyMs: Math.round(performance.now() - startedAt),
          });
        }
        return { Category: pendingUpload.Category, FileStatus: FileStatus.Uploading, FileSize: pendingUpload!.FileSize, UploadProgress: 100 };
      } catch (error: any) {
        if (user) {
          trackTelemetryEvent({
            userSub: user.userId,
            userEmail: user.email,
            userGroups: getUserGroupsFromIdToken(user.idToken),
            authToken: user.idToken,
            actionType: 'file_upload',
            actionTarget: params.Key,
            status: 'failure',
            latencyMs: Math.round(performance.now() - startedAt),
            extra: { error: String(error?.message || error) },
          });
        }
        throw error;
      }
  };

  // TODO - refactor - new type instead of IFileUpload, more generic - otherwise i'm donwloading silver by referencing bronzepath
  const listObjectsAtPrefix = async (prefix:string): Promise<Array<IFileUpload>> => {

    const command = new ListObjectsV2Command({
      Bucket: props.bucketName,
      MaxKeys: S3_MAX_KEYS_TO_LIST,
      Prefix: prefix
    });

    let isTruncated = true;
    const s3KeysArr = [] as Array<IFileUpload>;
    while (isTruncated) {
      const { Contents, IsTruncated, NextContinuationToken } = await s3Ref.current.send(command);
      isTruncated = IsTruncated;
      command.input.ContinuationToken = NextContinuationToken;
      if(!Contents){
        console.error(`useS3Manager.listObjectsAtPrefix :: Contents not found`);
      } else{
        Contents.forEach( (c:any) => {
          s3KeysArr.push({ Category: c.Key.split('/').pop(), BronzePath:c.Key, FileStatus: FileStatus.Collected, FileSize:c.Size, UploadProgress:0 });
        });
      }

    }
    return s3KeysArr;

  };

  const downloadObject = async (key: string, customFilename?: string): Promise<void> => {
    
    const params = {
      Bucket: props.bucketName,
      Key: key,
    };
    
    const command = new GetObjectCommand(params);
    const data = await s3Ref.current.send(command);
    
    // Convert a readable stream to a blob
    const response = new Response(data.Body);
    const blob = await response.blob();
    
    // Use custom filename if provided, otherwise extract from key
    const filename = customFilename || key.split('/').pop() as string;
    downloadBlob(blob, filename);

    return;
  };

  const downloadJsonFile = async (key: string): Promise<string> => {

    let result = '';
    if (!s3Ref.current) return result;
    const params = { Bucket: props.bucketName, Key: key };

    try {

      const command = new GetObjectCommand(params);
      const data = await s3Ref.current.send(command);
      result = await new Response(data.Body).text();
      
    } catch (error) {
      console.log(`useS3Manager.downloadJsonFile :: error: ${error}`)
    }

    return result;
  };
  
  const setFileForUpload = (file: File | null) => {
    if (!fileUploads) return;
    const files = [...fileUploads];
    files[0].FileForUpload = file;
    files[0].FileStatus = (file) ? FileStatus.Collected : FileStatus.Empty;
    setFileUploads(files);
  };

  const setFileUploadStatus = (uploadProgress: number) => {
    const COMPLETED_UPLOAD_PROGRESS = 100.0;
    if (!fileUploads) return;
    const files = [...fileUploads];
    const index = 0;
    files[index].UploadProgress = (Math.round(uploadProgress * 100) / 100) * 100;
    files[index].FileStatus = FileStatus.Uploading;
    console.log(`useS3Manager.setFileUploadStatus :: updating ${files[index].Category} at ${files[index].UploadProgress}%`);
    setFileUploads(files);
  };

  // Public status setter used by snapshot-add-update mapping effect. Downgrade & race guards:
  // - If currently Uploading, do not override (await upload cycle completion)
  // - Do not downgrade Uploaded to Processing or Collected
  // - Allow transitions: Collected -> Processing/Uploaded/Error, Uploading -> Processing/Uploaded/Error (post completion via effect), Processing -> Uploaded/Error, Uploaded -> Error (edge case) remain; others ignored.
  const setFileStatus = (status: FileStatus) => {
    if (!fileUploads) return;
    const files = [...fileUploads];
    const index = 0;
    const current = files[index].FileStatus;
    // Guard: ignore while actively uploading (upload progress listener manages state)
    if (current === FileStatus.Uploading) return;
    // Guard: prevent Uploaded -> Processing downgrade
    if (current === FileStatus.Uploaded && status === FileStatus.Processing) return;
    // Idempotent
    if (current === status) return;
    files[index].FileStatus = status;
    setFileUploads(files);
  };

  const getObjectsForBucket = async (bucket: string, prefix: string): Promise<Array<string>> => {
    
    if (!s3Ref.current) return [];

    const fileAndFolderNames: string[] = [];
    let continuationToken: string | undefined = undefined;    

    do {

      const params = {
        Bucket: bucket,
        Prefix: prefix,
        ContinuationToken: continuationToken,
      };

      console.log(`useS3Manager.getObjectsForBucket :: path: ${bucket}/${prefix}`);
  
      try {

        const command = new ListObjectsV2Command(params);
        const result: ListObjectsV2Output = await s3Ref.current.send(command);

        // Extract file and folder names from the result
        const objects = result.Contents || [];
        const names = objects.map(object => object.Key || '');
        fileAndFolderNames.push(...names);
  
        // Set the continuation token for the next iteration
        continuationToken = result.NextContinuationToken;

      } catch (error) {
        console.error(`useS3Manager.getObjectsForBucket :: Error listing files and folders: ${error}`);
        return fileAndFolderNames;
      }
    } while (continuationToken);  

    return fileAndFolderNames;
  };

  return {
    isS3initialized,
    uploadAll,
    fileUploads,
    uploadSingle,
    isProcessing,
    setFileForUpload,
    setFileStatus,
    downloadObject,
    downloadJsonFile,
    listObjectsAtPrefix,
    getObjectsForBucket
  };
};


const getS3Key = (clientId: string, academicYear: string, snapshotDate: string, fileForUpload: File ) => {
  const ext = fileForUpload.name.split('.').pop()?.toLowerCase();
  if (!ext) {
    console.error('useAmaS3Manager.getS3Key :: extension not found');
    return '';
  }
  if (ext !== 'csv' && ext !== 'sav') {
    console.error(`useAmaS3Manager.getS3Key :: unsupported extension ${ext}`);
    return '';
  }
  const baseName = `${clientId}-${academicYear}-${snapshotDate}`;
  return `client_id=${clientId}/academic_year=${academicYear}/snapshot_date=${snapshotDate}/${baseName}.${ext}`;
};

const downloadBlob = (blob: Blob, filename: string) => {

  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob);
  // Create a link element
  const link = document.createElement('a');
  // Set link's href to point to the Blob URL
  link.href = blobUrl;
  //TODO: Set file extension
  // link.download = filename+'.csv';
  link.download = filename;
  // Append link to the body
  document.body.appendChild(link);
  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );

  // Remove link from body
  document.body.removeChild(link);
}